import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Users, TrendingUp, Calendar, Clock, MapPin } from "lucide-react";
import { DetailPageSkeleton } from "@/components/skeletons/PageSkeletons";

function pctColor(pct) {
  if (pct >= 90) return "text-[#059669]";
  if (pct >= 75) return "text-[#C8102E]";
  if (pct >= 60) return "text-[#D97706]";
  return "text-[#E11D48]";
}
function pctBar(pct) {
  if (pct >= 90) return "bg-[#059669]";
  if (pct >= 75) return "bg-[#C8102E]";
  if (pct >= 60) return "bg-[#D97706]";
  return "bg-[#E11D48]";
}

export default function ClassDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get(`/classes/${id}`).then(r => setData(r.data)); }, [id]);

  if (!data) return <DetailPageSkeleton testId="class-loading" />;

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#0F172A] mb-4" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">{data.class.course_code} · Section {data.class.section_name}</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2" data-testid="class-heading">{data.class.course_name}</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 border-t border-l border-[#E2E8F0] bg-white">
        <div className="border-r border-b border-[#E2E8F0] p-6" data-testid="class-stat-overall">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]"><TrendingUp className="w-3.5 h-3.5" /> Overall</div>
          <div className={`font-heading text-3xl font-extrabold tracking-tighter mt-2 ${pctColor(data.overall_pct)}`}>{data.overall_pct}%</div>
        </div>
        <div className="border-r border-b border-[#E2E8F0] p-6" data-testid="class-stat-students">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]"><Users className="w-3.5 h-3.5" /> Students</div>
          <div className="font-heading text-3xl font-extrabold tracking-tighter text-[#0F172A] mt-2">{data.students.length}</div>
        </div>
        <div className="border-r border-b border-[#E2E8F0] p-6" data-testid="class-stat-sessions">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]"><Calendar className="w-3.5 h-3.5" /> Sessions</div>
          <div className="font-heading text-3xl font-extrabold tracking-tighter text-[#0F172A] mt-2">{data.session_count}</div>
        </div>
      </div>

      {/* Timetable slots for this class */}
      {data.slots && data.slots.length > 0 && (
        <div className="px-6 md:px-12 py-8">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A] mb-4">Scheduled periods</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="class-slots">
            {data.slots.map(s => (
              <div key={s.id} className="bg-white border border-[#E2E8F0] rounded-lg px-3 py-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C8102E]">{s.day}</div>
                <div className="flex items-center gap-1 text-sm text-[#0F172A] font-semibold mt-1"><Clock className="w-3 h-3 text-[#94A3B8]" />{s.start_time}–{s.end_time}</div>
                {s.classroom_name && <div className="flex items-center gap-1 text-xs text-[#475569] mt-1"><MapPin className="w-3 h-3" />{s.classroom_name}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Students */}
      <div className="px-6 md:px-12 pb-16">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A] mb-6">Students</h2>
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          {data.students.map((s, i) => (
            <button
              key={s.id}
              onClick={() => navigate(`/students/${s.id}`)}
              className={`w-full text-left px-6 py-4 flex items-center gap-4 hover:bg-[#F1F5F9] transition-colors duration-200 ease-in-out ${i > 0 ? "border-t border-[#E2E8F0]" : ""}`}
              data-testid={`class-student-${s.id}`}
            >
              <div className="w-9 h-9 rounded-full bg-[#FDECEF] text-[#C8102E] flex items-center justify-center font-bold text-xs shrink-0">
                {s.name.split(" ").map(x => x[0]).slice(0,2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0F172A]">{s.name}</div>
                <div className="text-xs text-[#94A3B8]">Roll {s.roll_no} · {s.present_count}/{s.total_sessions} sessions</div>
              </div>
              <div className="hidden md:block w-48">
                <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div className={`h-full ${pctBar(s.attendance_pct)}`} style={{ width: `${s.attendance_pct}%` }} />
                </div>
              </div>
              <div className={`w-14 text-right font-heading font-bold ${pctColor(s.attendance_pct)}`}>{s.attendance_pct}%</div>
            </button>
          ))}
          {data.students.length === 0 && <div className="px-6 py-8 text-center text-[#94A3B8] text-sm">No students in this section yet.</div>}
        </div>
      </div>
    </div>
  );
}
